import {
  useEffect,
  useMemo,
  useCallback,
  useState,
  useRef,
  type DragEvent as ReactDragEvent,
} from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parse, isValid, isSameDay } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
// mobiscroll
import '@akoom-tech/mobiscroll-react/dist/css/mobiscroll.min.css';
import '../utils/jobScheduler.css';
import {
  Eventcalendar,
  MbscCalendarEvent,
  MbscCalendarEventData,
  MbscEventClickEvent,
  MbscEventCreateEvent,
  MbscEventUpdateEvent,
  MbscEventUpdatedEvent,
  MbscResource,
  MbscSelectedDateChangeEvent,
  snackbar,
  toast,
} from '@akoom-tech/mobiscroll-react';
// components
import {
  Alert,
  Box,
  Divider,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CleaningJobDetailsDialog,
  SchedulerPageSkeleton,
  UnassignedEventsContainer,
  ProviderOverlapsPanel,
} from '../components';
import {
  SuggestedProvidersCard,
  CoverageSummary,
} from '../components/recommendations';
// custom mobiscroll components
import {
  MbscCalendarHeader,
  MbscCleanerCell,
  MbscSchedulerEventCard,
  MbscSchedulerTimeOffBlock,
} from '../components/mbsc-custom-components';
// API
import {
  getSchedulerBookingSessions,
  updateBookingSession,
} from '@/features/bookings-management/api';
import {
  buildClientCleanerRestrictionWarningMessage,
  getClientCleanerRestrictionWarnings,
  showClientCleanerRestrictionWarnings,
} from '@/features/bookings-management/utils';
import {
  getActiveProviders,
  getProvidersTimeOff,
} from '@/features/users/shared/api';
// utils + hooks
import useToggle from '@/hooks/useToggle';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
//
import {
  applySortFilterCleaningJobs,
  formatDateToISOString,
  formatMbscDateType,
  getNextPrevDay,
  mbscCalendarConfig,
  parseBookingSessionData,
  retrieveCleaner,
  schedulerSx,
  transformBookingSession,
  buildSchedulerProviderTimeOffEvents,
  calculateProviderLoadPercentage,
  type SchedulerBookingTypeFilter,
  type SchedulerIssueFilter,
  type SchedulerSessionStatusFilter,
} from '../utils';
import {
  buildCoverageSummary,
  buildMatchRequestPayload,
  filterAvailableMatches,
} from '../utils/coverage';
import {
  ProviderMatchBatchRequest,
  ProviderMatchBatchResponseItem,
  ProviderMatchResult,
  getProviderMatchesBatch,
} from '@/features/users/providers/api/matches';
// types
import { EventcalendarBase } from '@akoom-tech/mobiscroll-react/dist/src/core/components/eventcalendar/eventcalendar';
import { TransformedBooking } from '../types';
import { BookingSession } from '@/features/bookings-management/types';
import { ProviderTimeOff, User } from '@/features/users/shared/types';

// =====================================================================================

type CleanerResource = User & {
  schedulerAssignedMinutes: number;
  schedulerAssignedHours: number;
  schedulerUtilisation: number;
  schedulerLabel: string;
  isHighlighted?: boolean;
  cssClass?: string;
  background?: string;
};

type BookingWithMatchMeta = TransformedBooking & {
  matchResult?: ProviderMatchBatchResponseItem;
  coverageSummary?: CoverageSummary | null;
  pinnedProviders: ProviderMatchResult[];
};

const DATE_URL_FORMAT = 'dd-MM-yyyy';
const HIGHLIGHT_PROVIDER_PARAM = 'highlightProvider';
const LEGACY_HIGHLIGHT_PROVIDER_PARAM = 'highlight_provider';
const RESOURCE_HIGHLIGHT_CLASS = 'scheduler-resource-highlight';
const TIME_OFF_DEBUG_PARAM = 'debugTimeOff';
const TIME_OFF_DEBUG_STORAGE_KEY = 'schedulerDebugTimeOff';
const TIME_OFF_DEBUG_LABEL = '[Scheduler time off debug]';

const hasDebugToken = (value: string | null, token: string) =>
  (value ?? '')
    .split(',')
    .map((part) => part.trim().toLowerCase())
    .includes(token);

const isStoredTimeOffDebugEnabled = () => {
  try {
    return (
      typeof window !== 'undefined' &&
      window.localStorage.getItem(TIME_OFF_DEBUG_STORAGE_KEY) === '1'
    );
  } catch {
    return false;
  }
};

export default function JobScheduler() {
  const { errorMsg, warningMsg } = useSnackbarMsg();
  const theme = useTheme();
  const schedulerViewConfig = useMemo(() => mbscCalendarConfig(), []);
  const highlightedResourceBackground = useMemo(
    () => alpha(theme.palette.info.main, 0.14),
    [theme.palette.info.main]
  );

  // =============================================
  //    SELECTED EVENT + CLEANER + DATE CHANGE
  // =============================================
  // This is used used to store the currently selected date which can be changed in the header.
  // It is also used as a query key. Changing this will trigger a refetch of the booking sessions.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDateParam = searchParams.get('date');
  const isTimeOffDebugEnabled =
    searchParams.get(TIME_OFF_DEBUG_PARAM) === '1' ||
    hasDebugToken(searchParams.get('debug'), 'timeoff') ||
    isStoredTimeOffDebugEnabled();
  const highlightProviderParam =
    searchParams.get(HIGHLIGHT_PROVIDER_PARAM) ??
    searchParams.get(LEGACY_HIGHLIGHT_PROVIDER_PARAM);
  const lastSyncedDateParam = useRef<string | null>(selectedDateParam);

  const highlightProviderId = useMemo(() => {
    if (!highlightProviderParam) return null;
    const parsed = Number(highlightProviderParam);
    return Number.isFinite(parsed) ? parsed : null;
  }, [highlightProviderParam]);

  const initialDate = useMemo(() => {
    if (selectedDateParam) {
      const parsed = parse(selectedDateParam, DATE_URL_FORMAT, new Date());
      if (isValid(parsed)) {
        return parsed;
      }
    }
    return new Date();
  }, [DATE_URL_FORMAT, selectedDateParam]);

  const [selectedDate, setSelectedDate] = useState<Date>(initialDate);
  const isSyncingDateFromUrl = useRef(false);

  useEffect(() => {
    if (selectedDateParam === lastSyncedDateParam.current) {
      return;
    }
    lastSyncedDateParam.current = selectedDateParam;

    if (!selectedDateParam) return;
    const parsed = parse(selectedDateParam, DATE_URL_FORMAT, new Date());
    if (isValid(parsed) && !isSameDay(parsed, selectedDate)) {
      isSyncingDateFromUrl.current = true;
      setSelectedDate(parsed);
    }
  }, [DATE_URL_FORMAT, selectedDateParam, selectedDate]);

  useEffect(() => {
    if (isSyncingDateFromUrl.current) {
      isSyncingDateFromUrl.current = false;
      return;
    }
    const currentDateStr = format(selectedDate, DATE_URL_FORMAT);
    if (currentDateStr !== selectedDateParam) {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set('date', currentDateStr);
        return params;
      });
    }
  }, [DATE_URL_FORMAT, selectedDate, selectedDateParam, setSearchParams]);

  const handleClearHighlightProvider = useCallback(() => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete(HIGHLIGHT_PROVIDER_PARAM);
      params.delete(LEGACY_HIGHLIGHT_PROVIDER_PARAM);
      return params;
    });
  }, [setSearchParams]);

  const selectedDateKey = useMemo(
    () => format(selectedDate, 'yyyy-MM-dd'),
    [selectedDate]
  );

  const schedulerQueryKey = useMemo(
    () => ['scheduler', selectedDateKey] as const,
    [selectedDateKey]
  );

  const [currentSessionId, setCurrentSessionId] = useState<number | null>(null);
  const [selectedCleanerId, setSelectedCleanerId] = useState<number>(0);
  const [selectedStatusFilters, setSelectedStatusFilters] = useState<
    SchedulerSessionStatusFilter[]
  >([]);
  const [selectedBookingTypeFilters, setSelectedBookingTypeFilters] = useState<
    SchedulerBookingTypeFilter[]
  >([]);
  const [selectedIssueFilters, setSelectedIssueFilters] = useState<
    SchedulerIssueFilter[]
  >([]);
  const [selectedBookingId, setSelectedBookingId] = useState<number | null>(
    null
  );
  const [manuallySelectedBooking, setManuallySelectedBooking] =
    useState<TransformedBooking | null>(null);
  const [pendingAssignmentBookingIds, setPendingAssignmentBookingIds] =
    useState<number[]>([]);
  const [pinnedProvidersByBooking, setPinnedProvidersByBooking] = useState<
    Record<number, ProviderMatchResult[]>
  >({});
  const [showSuggestionsPanel, setShowSuggestionsPanel] = useState(true);
  const [quickActionTab, setQuickActionTab] = useState<'assign' | 'overlaps'>(
    'assign'
  );
  const [
    schedulerRestrictionWarningMessages,
    setSchedulerRestrictionWarningMessages,
  ] = useState<string[]>([]);
  const recommendationsCardRef = useRef<HTMLDivElement | null>(null);
  const schedulerScrollRef = useRef<HTMLDivElement | null>(null);
  const schedulerScrollQueryRef = useRef<NodeJS.Timeout | null>(null);
  const schedulerAutoScrollFrame = useRef<number | null>(null);

  const stopSchedulerAutoScroll = useCallback(() => {
    if (schedulerAutoScrollFrame.current !== null) {
      cancelAnimationFrame(schedulerAutoScrollFrame.current);
      schedulerAutoScrollFrame.current = null;
    }
  }, []);

  const attachSchedulerScrollEl = useCallback(() => {
    const maybeEl =
      document.querySelector<HTMLDivElement>('.mbsc-timeline-scroll') ??
      document.querySelector<HTMLDivElement>('.mbsc-schedule-wrapper');
    if (maybeEl) {
      schedulerScrollRef.current = maybeEl;
      return;
    }
  }, []);

  const handleSchedulerDragOver = useCallback(
    (event: ReactDragEvent<HTMLDivElement>) => {
      const container = schedulerScrollRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const threshold = 80;
      const scrollStep = 18;

      const isNearTop = event.clientY < rect.top + threshold;
      const isNearBottom = event.clientY > rect.bottom - threshold;

      if (!isNearTop && !isNearBottom) {
        stopSchedulerAutoScroll();
        return;
      }

      // Allow dropping while dragging over the scheduler
      event.preventDefault();

      const direction = isNearTop ? -1 : 1;

      if (schedulerAutoScrollFrame.current === null) {
        const step = () => {
          const el = schedulerScrollRef.current;
          if (!el) {
            stopSchedulerAutoScroll();
            return;
          }
          el.scrollTop += direction * scrollStep;
          schedulerAutoScrollFrame.current = requestAnimationFrame(step);
        };
        schedulerAutoScrollFrame.current = requestAnimationFrame(step);
      }
    },
    [stopSchedulerAutoScroll]
  );

  // =================================
  //     REACT QUERY / FETCH DATA
  // =================================
  const queryClient = useQueryClient();

  const nextDay = getNextPrevDay(selectedDate, 'next');
  const prevDay = getNextPrevDay(selectedDate, 'prev');
  const { data: bookingSessions, isLoading: isBookingSessionsDataLoading } =
    useQuery<BookingSession[], unknown, TransformedBooking[]>({
      queryKey: schedulerQueryKey, // when selectedDate changes the cache gets invalidated -- make sure to include all the querykeys that are used here
      queryFn: () =>
        getSchedulerBookingSessions(`?startDate=${prevDay}&endDate=${nextDay}`),
      onError: (error) => errorMsg(error, 'Error fetching cleaning jobs'),
      select: (data) => parseBookingSessionData(data) || [],
    });

  const addPendingAssignment = useCallback((bookingId: number) => {
    setPendingAssignmentBookingIds((prev) =>
      prev.includes(bookingId) ? prev : [...prev, bookingId]
    );
  }, []);

  const removePendingAssignment = useCallback((bookingId: number) => {
    setPendingAssignmentBookingIds((prev) =>
      prev.filter((id) => id !== bookingId)
    );
  }, []);

  const clearManualSelection = useCallback(() => {
    setManuallySelectedBooking(null);
  }, []);

  const { data: allCleaners } = useQuery({
    queryKey: ['cleaners'],
    queryFn: getActiveProviders,
    onError: (error) => errorMsg(error, 'Error fetching cleaners'),
  });

  const highlightedProviderLabel = useMemo(() => {
    if (!highlightProviderId || !allCleaners) return null;
    const matched = allCleaners.find(
      (cleaner) => Number(cleaner.id) === Number(highlightProviderId)
    );
    if (!matched) return `Provider #${highlightProviderId}`;
    const name = `${matched.first_name ?? ''} ${
      matched.last_name ?? ''
    }`.trim();
    return name || `Provider #${highlightProviderId}`;
  }, [allCleaners, highlightProviderId]);

  const dayWindow = useMemo(() => {
    const start = new Date(selectedDate);
    start.setHours(6, 0, 0, 0);
    const end = new Date(selectedDate);
    end.setHours(23, 0, 0, 0);
    return { start, end };
  }, [selectedDate]);

  const cleanersWithStats = useMemo<CleanerResource[] | undefined>(() => {
    if (!allCleaners) return undefined;
    const events = bookingSessions ?? [];

    const formatHours = (hours: number) => {
      const rounded = Math.round(hours * 10) / 10;
      return Number.isInteger(rounded) ? `${rounded}` : rounded.toFixed(1);
    };

    const withStats = allCleaners.map((cleaner) => {
      const assignedMinutes = events.reduce((total, event) => {
        if (Number(event.resource) !== cleaner.id) return total;
        const eventStart = new Date(event.start);
        const eventEnd = new Date(event.end);
        if (
          Number.isNaN(eventStart.getTime()) ||
          Number.isNaN(eventEnd.getTime())
        ) {
          return total;
        }

        const overlapStart =
          eventStart > dayWindow.start ? eventStart : dayWindow.start;
        const overlapEnd = eventEnd < dayWindow.end ? eventEnd : dayWindow.end;

        if (overlapEnd <= overlapStart) {
          return total;
        }

        return total + (overlapEnd.getTime() - overlapStart.getTime()) / 60000;
      }, 0);

      const assignedHours = assignedMinutes / 60;
      const utilisation = calculateProviderLoadPercentage(assignedMinutes);
      const label = `${formatHours(assignedHours)}h (${Math.round(
        utilisation
      )}%)`;

      return {
        ...cleaner,
        schedulerAssignedMinutes: assignedMinutes,
        schedulerAssignedHours: assignedHours,
        schedulerUtilisation: utilisation,
        schedulerLabel: label,
      };
    });

    const normalizeName = (value?: string | null) =>
      value?.trim().toLowerCase() ?? '';
    const buildDisplayName = (
      firstName?: string | null,
      lastName?: string | null
    ) => `${firstName ?? ''} ${lastName ?? ''}`.trim();

    return withStats.sort((a, b) => {
      const minutesDiff =
        b.schedulerAssignedMinutes - a.schedulerAssignedMinutes;
      if (minutesDiff !== 0) return minutesDiff;

      const nameDiff = normalizeName(
        buildDisplayName(a.first_name, a.last_name)
      ).localeCompare(
        normalizeName(buildDisplayName(b.first_name, b.last_name))
      );
      if (nameDiff !== 0) return nameDiff;

      return Number(a.id) - Number(b.id);
    });
  }, [allCleaners, bookingSessions, dayWindow.end, dayWindow.start]);

  const { data: providerTimeOff } = useQuery<ProviderTimeOff[]>({
    queryKey: ['providerTimeOff', prevDay, nextDay],
    queryFn: () =>
      getProvidersTimeOff({ startDate: prevDay, endDate: nextDay }),
    onError: (error) => errorMsg(error, 'Error fetching cleaner time off'),
  });

  const providerTimeOffInvalidEvents = useMemo<MbscCalendarEvent[]>(() => {
    return buildSchedulerProviderTimeOffEvents(providerTimeOff, {
      rangeStart: prevDay,
      rangeEnd: nextDay,
    });
  }, [nextDay, prevDay, providerTimeOff]);

  const toDateOrNull = (value: unknown): Date | null => {
    if (value instanceof Date) return new Date(value);
    if (typeof value === 'string' || typeof value === 'number') {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  };

  const hasTimeOffOverlap = useCallback(
    (resourceId: number, startValue?: unknown, endValue?: unknown) => {
      const startDate = toDateOrNull(startValue);
      const endDate = toDateOrNull(endValue);
      if (!resourceId || !startDate || !endDate) return false;

      if (
        Number.isNaN(startDate.getTime()) ||
        Number.isNaN(endDate.getTime())
      ) {
        return false;
      }

      return providerTimeOffInvalidEvents.some((block) => {
        const blockResource = Number(block.resource);
        if (!Number.isFinite(blockResource) || blockResource !== resourceId) {
          return false;
        }

        const blockStart = toDateOrNull(block.start);
        const blockEnd = toDateOrNull(block.end);

        if (!blockStart || !blockEnd) {
          return false;
        }

        return startDate < blockEnd && endDate > blockStart;
      });
    },
    [providerTimeOffInvalidEvents]
  );

  // =================================
  //     REACT QUERY / UPDATE DATA
  // =================================

  // more info on useMutation --> https://www.youtube.com/watch?v=DocXo3gqGdI&t=4431s
  const updateBookingSessionMutation = useMutation({
    // mutate function will make the API call
    mutationFn: (updatedSession: {
      id: number;
      new_cleaner: number;
      old_cleaner: number;
    }) =>
      updateBookingSession(updatedSession.id, {
        booking_cleaners:
          updatedSession.new_cleaner === 0 ? [] : [updatedSession.new_cleaner],
        // include_unassigned: true,
      }),
    // onMutate fires before the mutationFn and optimistically updates the cache
    onMutate: (updatedSessionFromScheduler) => {
      const oldSchedulerData = queryClient.getQueryData(schedulerQueryKey);

      if (queryClient.getQueryData(schedulerQueryKey)) {
        // TODO: TS any
        queryClient.setQueryData(schedulerQueryKey, (old: any) =>
          old.map((b: any) =>
            // here we need to transform the date/time again
            b.id === updatedSessionFromScheduler.id
              ? {
                  ...b,
                  start: formatDateToISOString(b.start),
                  end: formatDateToISOString(b.end),
                  booking_session_cleaners: [
                    retrieveCleaner(
                      updatedSessionFromScheduler.new_cleaner,
                      allCleaners,
                      true
                    ),
                  ],
                }
              : b
          )
        );
      }

      if (
        updatedSessionFromScheduler.old_cleaner === 0 &&
        updatedSessionFromScheduler.new_cleaner
      ) {
        addPendingAssignment(updatedSessionFromScheduler.id);
      }

      // this will be used in the onError callback
      return () =>
        queryClient.setQueryData(schedulerQueryKey, oldSchedulerData);
    },

    onSuccess: (updatedSessionFromApi) => {
      showClientCleanerRestrictionWarnings(
        updatedSessionFromApi,
        warningMsg,
        allCleaners
      );
      const restrictionWarningMessages = getClientCleanerRestrictionWarnings(
        updatedSessionFromApi
      ).map((warning) => {
        const sessionLabel = updatedSessionFromApi?.id
          ? `Session #${updatedSessionFromApi.id}: `
          : '';

        return `${sessionLabel}${buildClientCleanerRestrictionWarningMessage(
          warning,
          allCleaners
        )}`;
      });
      setSchedulerRestrictionWarningMessages(restrictionWarningMessages);

      if (queryClient.getQueryData(schedulerQueryKey)) {
        // if we have query data for posts we perform this operation
        queryClient.setQueryData(schedulerQueryKey, (old: any) =>
          old.map((b: any) =>
            b.id === updatedSessionFromApi.id
              ? transformBookingSession(updatedSessionFromApi)
              : b
          )
        );
      } else {
        // if there was no query for posts yet, we create a new array with the current updated booking
        queryClient.setQueryData(schedulerQueryKey, [updatedSessionFromApi]);
      }
    },

    onError: (error, variables, rollback) => {
      // this fn is returned from onMutate callback
      rollback && rollback();
      if (variables?.old_cleaner === 0) {
        removePendingAssignment(variables.id);
      }
      errorMsg(error, `Something went wrong while updating booking`);
    },

    onSettled: (_data, error, mutationData) => {
      // if mutation was successful, we show a snackbar with the option to undo the mutation
      if (!!!error) {
        snackbar({
          message: `#${mutationData.id} assigned to ${retrieveCleaner(
            mutationData.new_cleaner,
            allCleaners
          )}`,
          color: 'success',
          button: {
            action: () => {
              updateBookingSessionMutation.mutate({
                id: mutationData.id,
                new_cleaner: mutationData.old_cleaner,
                old_cleaner: mutationData.new_cleaner,
              });
            },
            text: 'Undo',
          },
          duration: 6000,
        });
      }
      queryClient.invalidateQueries(schedulerQueryKey);
      queryClient.invalidateQueries(['unassignedJobs']);
      queryClient.invalidateQueries({ queryKey: ['providerMatchesBatch'] });
      queryClient.invalidateQueries({ queryKey: ['unassignedMatches'] });
      if (
        !error &&
        mutationData?.id &&
        manuallySelectedBooking?.id === mutationData.id
      ) {
        clearManualSelection();
      }
    },
  });

  // ==================================================
  //    FILTER CLEANER / SET DATE IN CALENDAR HEADER
  // ==================================================
  const handleCleanerFilter = useCallback((cleanerId: number) => {
    setSelectedCleanerId(cleanerId || 0);
  }, []);

  const handleStatusFilterToggle = useCallback(
    (status: SchedulerSessionStatusFilter) => {
      setSelectedStatusFilters((prev) =>
        prev.includes(status)
          ? prev.filter((item) => item !== status)
          : [...prev, status]
      );
    },
    []
  );

  const handleBookingTypeFilterToggle = useCallback(
    (bookingType: SchedulerBookingTypeFilter) => {
      setSelectedBookingTypeFilters((prev) =>
        prev.includes(bookingType)
          ? prev.filter((item) => item !== bookingType)
          : [...prev, bookingType]
      );
    },
    []
  );

  const handleIssueFilterToggle = useCallback((issue: SchedulerIssueFilter) => {
    setSelectedIssueFilters((prev) =>
      prev.includes(issue)
        ? prev.filter((item) => item !== issue)
        : [...prev, issue]
    );
  }, []);

  const filteredData = applySortFilterCleaningJobs({
    bookingSessions,
    cleaners: cleanersWithStats,
    cleanerId: selectedCleanerId,
    statusFilters: selectedStatusFilters,
    bookingTypeFilters: selectedBookingTypeFilters,
    issueFilters: selectedIssueFilters,
  });

  const filteredBookings: TransformedBooking[] = filteredData?.[0] ?? [];
  const filteredCleaners: CleanerResource[] = filteredData?.[1] ?? [];

  const resourcesWithHighlight = useMemo<MbscResource[]>(() => {
    return filteredCleaners.map((cleaner) => {
      const isHighlighted =
        highlightProviderId != null &&
        Number(cleaner.id) === Number(highlightProviderId);
      const cssClass = [
        cleaner.cssClass,
        isHighlighted ? RESOURCE_HIGHLIGHT_CLASS : null,
      ]
        .filter(Boolean)
        .join(' ')
        .trim();

      return {
        ...cleaner,
        isHighlighted,
        ...(cssClass ? { cssClass } : {}),
        ...(isHighlighted ? { background: highlightedResourceBackground } : {}),
      };
    });
  }, [filteredCleaners, highlightProviderId, highlightedResourceBackground]);

  const unassignedBookingsForSelectedDay = useMemo(() => {
    if (!filteredBookings.length) return [];

    const pendingIds = new Set(pendingAssignmentBookingIds);

    return filteredBookings.filter((booking) => {
      if (pendingIds.has(booking.id)) {
        return false;
      }

      const hasAssignedCleaner = Number(booking.resource) > 0;
      if (hasAssignedCleaner) {
        return false;
      }

      const bookingStart = toDateOrNull(booking.start);
      if (!bookingStart) {
        return false;
      }

      return isSameDay(bookingStart, selectedDate);
    });
  }, [filteredBookings, pendingAssignmentBookingIds, selectedDate]);

  useEffect(() => {
    setPendingAssignmentBookingIds((prev) => {
      if (!prev.length) {
        return prev;
      }

      const next = prev.filter((bookingId) => {
        const booking = filteredBookings.find((item) => item.id === bookingId);
        if (!booking) {
          return false;
        }
        return Number(booking.resource) === 0;
      });
      return next.length === prev.length ? prev : next;
    });
  }, [filteredBookings]);

  const hasBookingOverlap = useCallback(
    (
      resourceId: number,
      startValue?: unknown,
      endValue?: unknown,
      excludeBookingId?: number | string
    ) => {
      const startDate = toDateOrNull(startValue);
      const endDate = toDateOrNull(endValue);
      if (!resourceId || !startDate || !endDate) return false;
      if (
        Number.isNaN(startDate.getTime()) ||
        Number.isNaN(endDate.getTime())
      ) {
        return false;
      }

      return (bookingSessions ?? []).some((booking) => {
        if (Number(booking.resource) !== resourceId) return false;
        if (
          excludeBookingId &&
          Number(booking.id) === Number(excludeBookingId)
        ) {
          return false;
        }

        const bookingStart = toDateOrNull(booking.start);
        const bookingEnd = toDateOrNull(booking.end);
        if (!bookingStart || !bookingEnd) return false;

        return startDate < bookingEnd && endDate > bookingStart;
      });
    },
    [bookingSessions]
  );

  const baseMatchRequests = useMemo(() => {
    return unassignedBookingsForSelectedDay
      .map((booking) => buildMatchRequestPayload(booking))
      .filter((request): request is ProviderMatchBatchRequest =>
        Boolean(request)
      );
  }, [unassignedBookingsForSelectedDay]);

  const manualSelectionRequest = useMemo(() => {
    if (!manuallySelectedBooking) return null;

    const alreadyIncluded = unassignedBookingsForSelectedDay.some(
      (booking) => booking.id === manuallySelectedBooking.id
    );
    if (alreadyIncluded) {
      return null;
    }

    return buildMatchRequestPayload(manuallySelectedBooking);
  }, [manuallySelectedBooking, unassignedBookingsForSelectedDay]);

  const manualSelectionFallbackResult = useMemo(() => {
    if (!manuallySelectedBooking) return null;

    const alreadyIncluded = unassignedBookingsForSelectedDay.some(
      (booking) => booking.id === manuallySelectedBooking.id
    );
    if (alreadyIncluded) {
      return null;
    }

    if (buildMatchRequestPayload(manuallySelectedBooking)) {
      return null;
    }

    return {
      request: {
        reference: String(manuallySelectedBooking.id),
        outward_code: '',
        service_type: '',
      },
      matches: [],
      reason: 'Add a postcode and service type to see cleaner suggestions.',
    } as ProviderMatchBatchResponseItem;
  }, [manuallySelectedBooking, unassignedBookingsForSelectedDay]);

  const matchRequests = useMemo(() => {
    const dedup = new Map<string, ProviderMatchBatchRequest>();

    baseMatchRequests.forEach((request) => {
      if (request.reference) {
        dedup.set(request.reference, request);
      }
    });

    if (manualSelectionRequest?.reference) {
      dedup.set(manualSelectionRequest.reference, manualSelectionRequest);
    }

    return Array.from(dedup.values());
  }, [baseMatchRequests, manualSelectionRequest]);

  const matchRequestsKey = useMemo(() => {
    if (!matchRequests.length) {
      return 'none';
    }

    return matchRequests
      .map((request) =>
        [
          request.reference ?? '',
          request.outward_code,
          request.service_type,
          request.date ?? '',
          request.address_id ?? '',
          request.client_user_id ?? '',
          request.limit ?? '',
        ].join('-')
      )
      .join('|');
  }, [matchRequests]);

  const providerMatchesQueryKey = useMemo(
    () => ['providerMatchesBatch', selectedDateKey, matchRequestsKey],
    [matchRequestsKey, selectedDateKey]
  );

  const {
    data: providerMatchesBatch = [],
    isLoading: isProviderMatchesLoading,
    isFetching: isProviderMatchesFetching,
  } = useQuery({
    queryKey: providerMatchesQueryKey,
    queryFn: () => getProviderMatchesBatch(matchRequests),
    enabled: matchRequests.length > 0,
    staleTime: 1000 * 60 * 5,
    keepPreviousData: true,
  });

  const matchesByReference = useMemo(() => {
    const map: Record<number, ProviderMatchBatchResponseItem> = {};

    providerMatchesBatch.forEach((entry) => {
      const ref = entry.request?.reference;
      if (!ref) return;
      const bookingId = Number(ref);
      if (Number.isNaN(bookingId)) return;
      const availableMatches = filterAvailableMatches(entry.matches);
      const orderedMatches = [...availableMatches].sort((a, b) => {
        const aCount = a.assigned_bookings_at_address ?? 0;
        const bCount = b.assigned_bookings_at_address ?? 0;
        if (aCount !== bCount) {
          return bCount - aCount;
        }
        return a.provider_id - b.provider_id;
      });
      map[bookingId] = {
        ...entry,
        matches: orderedMatches,
        reason:
          availableMatches.length === 0
            ? entry.reason ||
              'All matching cleaners are busy for this timeslot.'
            : entry.reason,
      };
    });

    if (manualSelectionFallbackResult && manuallySelectedBooking) {
      map[manuallySelectedBooking.id] = manualSelectionFallbackResult;
    }

    return map;
  }, [
    manualSelectionFallbackResult,
    manuallySelectedBooking,
    providerMatchesBatch,
  ]);

  const augmentBookingWithMatches = useCallback(
    (booking: TransformedBooking): BookingWithMatchMeta => ({
      ...booking,
      matchResult: matchesByReference[booking.id],
      coverageSummary: buildCoverageSummary(matchesByReference[booking.id]),
      pinnedProviders: pinnedProvidersByBooking[booking.id] ?? [],
    }),
    [matchesByReference, pinnedProvidersByBooking]
  );

  const bookingsWithMatchMeta = useMemo<BookingWithMatchMeta[]>(
    () => filteredBookings.map((booking) => augmentBookingWithMatches(booking)),
    [augmentBookingWithMatches, filteredBookings]
  );

  const visibleProviderTimeOffEvents = useMemo<MbscCalendarEvent[]>(() => {
    if (!providerTimeOffInvalidEvents.length) return [];
    if (!selectedCleanerId) return providerTimeOffInvalidEvents;

    return providerTimeOffInvalidEvents.filter(
      (event) => Number(event.resource) === selectedCleanerId
    );
  }, [providerTimeOffInvalidEvents, selectedCleanerId]);

  const schedulerEvents = useMemo<MbscCalendarEvent[]>(
    () => [...visibleProviderTimeOffEvents, ...bookingsWithMatchMeta],
    [bookingsWithMatchMeta, visibleProviderTimeOffEvents]
  );

  /* eslint-disable no-console */
  useEffect(() => {
    if (!isTimeOffDebugEnabled) return;

    const resourceRows = resourcesWithHighlight.map((resource) => {
      const firstName = resource.first_name as string | undefined;
      const lastName = resource.last_name as string | undefined;
      const fallbackName = `${firstName ?? ''} ${lastName ?? ''}`.trim();

      return {
        id: Number(resource.id),
        rawId: resource.id,
        name: resource.name ?? fallbackName,
        cssClass: resource.cssClass,
      };
    });
    const visibleResourceIds = new Set(resourceRows.map((row) => row.id));
    const selectedResource = selectedCleanerId
      ? resourceRows.find((resource) => resource.id === selectedCleanerId)
      : null;

    const rawRows = (providerTimeOff ?? []).map((timeOff) => {
      const rawProviderId =
        timeOff.provider?.id ?? timeOff.user_id ?? timeOff.provider_id ?? null;
      const providerName = timeOff.provider
        ? `${timeOff.provider.first_name ?? ''} ${
            timeOff.provider.last_name ?? ''
          }`.trim()
        : '';

      return {
        id: timeOff.id,
        providerId: rawProviderId,
        providerName,
        start: timeOff.start_date,
        end: timeOff.end_date,
        type:
          timeOff.type?.description ??
          timeOff.offday_request_types?.description ??
          timeOff.type_id ??
          '',
        recurrence: timeOff.offday_recurrence
          ? timeOff.offday_recurrence.repeat_string
          : 'one-off',
        recurrenceDays: timeOff.offday_recurrence?.week_days ?? '',
        recurrenceFrom: timeOff.offday_recurrence?.from_date ?? '',
        recurrenceUntil: timeOff.offday_recurrence?.until ?? '',
      };
    });

    const invalidRows = providerTimeOffInvalidEvents.map((event) => {
      const resourceId = Number(event.resource);
      return {
        id: event.id,
        resource: event.resource,
        resourceVisible: visibleResourceIds.has(resourceId),
        start: event.start,
        end: event.end,
        title: event.title,
        cssClass: event.cssClass,
      };
    });

    const visibleRows = visibleProviderTimeOffEvents.map((event) => ({
      id: event.id,
      resource: event.resource,
      start: event.start,
      end: event.end,
      title: event.title,
    }));
    const unmatchedInvalidRows = invalidRows.filter(
      (row) => !row.resourceVisible
    );

    console.groupCollapsed(
      `${TIME_OFF_DEBUG_LABEL} ${selectedDateKey} raw=${rawRows.length} invalid=${invalidRows.length} visible=${visibleRows.length} data=${schedulerEvents.length}`
    );
    console.log('range/filter/resources', {
      prevDay,
      selectedDate: selectedDateKey,
      nextDay,
      selectedCleanerId,
      selectedResource,
      visibleResourceIds: Array.from(visibleResourceIds),
      schedulerEventCount: schedulerEvents.length,
      bookingEventCount: bookingsWithMatchMeta.length,
      timeOffEventCount: visibleProviderTimeOffEvents.length,
    });

    if (rawRows.length) {
      console.table(rawRows);
    } else {
      console.warn('No provider time off returned by the API for this range.');
    }

    if (invalidRows.length) {
      console.table(invalidRows);
    } else if (rawRows.length) {
      console.warn(
        'Provider time off was returned, but no Mobiscroll invalid ranges were generated.'
      );
    }

    if (visibleRows.length) {
      console.table(visibleRows);
    } else if (invalidRows.length && selectedCleanerId) {
      console.warn(
        'Mobiscroll invalid ranges exist, but the selected cleaner filter removed all of them.'
      );
    }

    if (unmatchedInvalidRows.length) {
      console.warn(
        'Some invalid ranges reference provider/resource ids that are not rendered in the current scheduler resources.',
        unmatchedInvalidRows
      );
    }
    console.groupEnd();
  }, [
    isTimeOffDebugEnabled,
    nextDay,
    prevDay,
    providerTimeOff,
    providerTimeOffInvalidEvents,
    resourcesWithHighlight,
    bookingsWithMatchMeta.length,
    schedulerEvents.length,
    selectedCleanerId,
    selectedDateKey,
    visibleProviderTimeOffEvents,
  ]);

  useEffect(() => {
    if (!isTimeOffDebugEnabled || typeof window === 'undefined') return;

    const frameId = window.requestAnimationFrame(() => {
      const schedulerRoot = schedulerScrollRef.current;
      const renderedInvalidNodes =
        schedulerRoot?.querySelectorAll(
          '.mbsc-timeline-invalid, .mbsc-schedule-invalid, .timeoff'
        ).length ?? 0;

      console.log(`${TIME_OFF_DEBUG_LABEL} rendered DOM`, {
        selectedDate: selectedDateKey,
        expectedTimeOffRanges: visibleProviderTimeOffEvents.length,
        expectedSchedulerEvents: schedulerEvents.length,
        renderedInvalidNodes,
      });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [
    isTimeOffDebugEnabled,
    schedulerEvents.length,
    selectedDateKey,
    visibleProviderTimeOffEvents.length,
  ]);
  /* eslint-enable no-console */

  useEffect(() => {
    clearManualSelection();
  }, [clearManualSelection, selectedDateKey]);

  useEffect(() => {
    if (!selectedBookingId) return;

    const exists = bookingsWithMatchMeta.some(
      (booking) => booking.id === selectedBookingId
    );

    if (!exists && !manuallySelectedBooking) {
      setSelectedBookingId(null);
    }
  }, [bookingsWithMatchMeta, manuallySelectedBooking, selectedBookingId]);

  useEffect(() => {
    if (selectedBookingId || manuallySelectedBooking) {
      return;
    }

    if (bookingsWithMatchMeta.length) {
      setSelectedBookingId(bookingsWithMatchMeta[0].id);
    }
  }, [bookingsWithMatchMeta, manuallySelectedBooking, selectedBookingId]);

  const selectedBooking = useMemo(() => {
    if (selectedBookingId) {
      const fromCalendar = bookingsWithMatchMeta.find(
        (booking) => booking.id === selectedBookingId
      );
      if (fromCalendar) {
        return fromCalendar;
      }
    }

    if (manuallySelectedBooking) {
      return augmentBookingWithMatches(manuallySelectedBooking);
    }

    return null;
  }, [
    augmentBookingWithMatches,
    bookingsWithMatchMeta,
    manuallySelectedBooking,
    selectedBookingId,
  ]);

  const selectedSummary = selectedBooking?.coverageSummary ?? null;

  const pendingMatchReferences = useMemo(() => {
    const refs = new Set<string>();
    matchRequests.forEach((request) => {
      if (request.reference) {
        refs.add(request.reference);
      }
    });
    return refs;
  }, [matchRequests]);

  const selectedMatchEntry = selectedBooking?.id
    ? matchesByReference[selectedBooking.id]
    : undefined;

  const selectedMatches = selectedMatchEntry?.matches ?? [];
  const selectedMatchesReason = selectedMatchEntry?.reason;

  const isSelectedMatchPending = Boolean(
    selectedBooking?.id &&
      pendingMatchReferences.has(String(selectedBooking.id)) &&
      !selectedMatchEntry &&
      matchRequests.length > 0 &&
      (isProviderMatchesLoading || isProviderMatchesFetching)
  );

  const selectedMatchesLoading = isSelectedMatchPending;

  const handlePinProvider = useCallback(
    (bookingId: number, provider: ProviderMatchResult) => {
      setPinnedProvidersByBooking((prev) => {
        const existing = prev[bookingId] ?? [];
        if (
          existing.some((item) => item.provider_id === provider.provider_id)
        ) {
          return prev;
        }
        return {
          ...prev,
          [bookingId]: [...existing, provider],
        };
      });
    },
    []
  );

  const handleUnpinProvider = useCallback(
    (bookingId: number, providerId: number) => {
      setPinnedProvidersByBooking((prev) => {
        const existing = prev[bookingId] ?? [];
        const updated = existing.filter(
          (provider) => provider.provider_id !== providerId
        );
        if (!updated.length) {
          const { [bookingId]: _, ...rest } = prev;
          return rest;
        }
        return {
          ...prev,
          [bookingId]: updated,
        };
      });
    },
    []
  );

  const scrollToSuggestions = useCallback(() => {
    recommendationsCardRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, []);

  const handleToggleSuggestionsPanel = useCallback(() => {
    setShowSuggestionsPanel((prev) => {
      const next = !prev;
      if (next) {
        setTimeout(() => {
          scrollToSuggestions();
        }, 100);
      }
      return next;
    });
  }, [scrollToSuggestions]);

  const handleSelectUnassignedSession = useCallback(
    (session: TransformedBooking) => {
      setSelectedBookingId(session.id);
      setManuallySelectedBooking(session);
      setQuickActionTab('assign');
      if (!showSuggestionsPanel) {
        setShowSuggestionsPanel(true);
      }
      scrollToSuggestions();
    },
    [scrollToSuggestions, showSuggestionsPanel]
  );

  const handleAssignSuggestedProvider = useCallback(
    (provider: ProviderMatchResult) => {
      if (!selectedBooking) return;
      updateBookingSessionMutation.mutate({
        id: Number(selectedBooking.id),
        new_cleaner: provider.provider_id,
        old_cleaner: Number(selectedBooking.resource) || 0,
      });
      if (!showSuggestionsPanel) {
        setShowSuggestionsPanel(true);
      }
    },
    [selectedBooking, updateBookingSessionMutation, showSuggestionsPanel]
  );

  const handleDateSelection = useCallback((date: Date) => {
    setSelectedDate(date);
  }, []);

  const handleSelectedDateChange = useCallback(
    (args: MbscSelectedDateChangeEvent) => {
      const { date } = args;
      const transformedDate = formatMbscDateType(date);
      setSelectedDate(transformedDate);
    },
    []
  );

  useEffect(() => {
    return () => {
      stopSchedulerAutoScroll();
    };
  }, [stopSchedulerAutoScroll]);

  useEffect(() => {
    attachSchedulerScrollEl();
    if (schedulerScrollQueryRef.current) {
      clearTimeout(schedulerScrollQueryRef.current);
    }
    // mobiscroll may render async; retry once shortly after render
    schedulerScrollQueryRef.current = setTimeout(attachSchedulerScrollEl, 150);
    return () => {
      if (schedulerScrollQueryRef.current) {
        clearTimeout(schedulerScrollQueryRef.current);
      }
    };
  }, [
    attachSchedulerScrollEl,
    selectedDateKey,
    schedulerEvents.length,
    visibleProviderTimeOffEvents.length,
  ]);

  // ====================
  //   EDIT JOB DIALOG
  // ====================

  const {
    toggle: isJobDialogOpen,
    onClose: closeJobDialog,
    onOpen: openJobDialog,
  } = useToggle();

  const handleEventClick = useCallback(
    (event: MbscEventClickEvent) => {
      if (event.event.isTimeOff) return;
      clearManualSelection();
      setSelectedBookingId(Number(event.event.id));
    },
    [clearManualSelection]
  );

  const handleEditSession = (event: MbscEventClickEvent) => {
    if (event.event.isTimeOff) return;
    setCurrentSessionId(Number(event.event.id));
    openJobDialog();
  };

  // =============================
  //    SCHEDULE EVENT/BOOKING
  // =============================
  const renderScheduleEvent = useCallback((data: MbscCalendarEventData) => {
    if (data.original?.isTimeOff) {
      return <MbscSchedulerTimeOffBlock title={data.title} />;
    }

    const bookingData = data.original as BookingWithMatchMeta | undefined;
    const pinnedProviders = bookingData?.pinnedProviders ?? [];
    const awaitingAcceptance = Boolean(bookingData?.awaitingProviderAcceptance);

    return (
      <MbscSchedulerEventCard
        data={data}
        awaitingAcceptance={awaitingAcceptance}
        pinnedProviders={pinnedProviders}
      />
    );
  }, []);

  // =================================
  //       MBSC CUSTOM COMPONENTS
  // =================================

  const renderHeader = useCallback(
    () => (
      <MbscCalendarHeader
        handleCleanerFilter={handleCleanerFilter}
        selectedDate={selectedDate}
        onSelectDate={handleDateSelection}
        highlightProviderLabel={
          highlightProviderId
            ? highlightedProviderLabel ?? `provider #${highlightProviderId}`
            : undefined
        }
        onClearHighlightProvider={
          highlightProviderId ? handleClearHighlightProvider : undefined
        }
        activeStatusFilters={selectedStatusFilters}
        onToggleStatusFilter={handleStatusFilterToggle}
        activeBookingTypeFilters={selectedBookingTypeFilters}
        onToggleBookingTypeFilter={handleBookingTypeFilterToggle}
        activeIssueFilters={selectedIssueFilters}
        onToggleIssueFilter={handleIssueFilterToggle}
      />
    ),
    [
      handleBookingTypeFilterToggle,
      handleCleanerFilter,
      handleClearHighlightProvider,
      handleDateSelection,
      handleIssueFilterToggle,
      handleStatusFilterToggle,
      highlightProviderId,
      highlightedProviderLabel,
      selectedBookingTypeFilters,
      selectedDate,
      selectedIssueFilters,
      selectedStatusFilters,
    ]
  );

  const renderResource = useCallback(
    (r: MbscResource) => <MbscCleanerCell cleanerResource={r} />,
    []
  );

  // =======================
  //     EVENT HANDLERS
  // =======================
  // this fires when drag and drop event between cleaners
  // that's where we update the database when the event is recurring
  const resolveCleanerId = useCallback(
    (resource: string | number | (string | number)[] | undefined) => {
      const resourceValue = Array.isArray(resource) ? resource[0] : resource;
      const parsedResource = Number(resourceValue);
      if (Number.isFinite(parsedResource) && parsedResource > 0) {
        return parsedResource;
      }

      return selectedCleanerId || 0;
    },
    [selectedCleanerId]
  );

  const eventUpdateFail = useCallback(() => {
    toast({
      message: `Cannot have overlapping jobs`,
      color: 'warning',
    });
  }, []);

  const handleEventUpdate = useCallback(
    (args: MbscEventUpdateEvent, inst: EventcalendarBase) => {
      const { event, oldEvent, newEvent } = args;

      const targetCleanerId = resolveCleanerId(
        newEvent?.resource ?? event.resource
      );
      const newStart = newEvent?.start ?? event.start;
      const newEnd = newEvent?.end ?? event.end;

      if (hasTimeOffOverlap(targetCleanerId, newStart, newEnd)) {
        eventUpdateFail();
        if (oldEvent) {
          inst.updateEvent(oldEvent);
        }
        return;
      }

      if (hasBookingOverlap(targetCleanerId, newStart, newEnd, event.id)) {
        eventUpdateFail();
        if (oldEvent) {
          inst.updateEvent(oldEvent);
        }
        return;
      }

      if (event.recurring) {
        updateBookingSessionMutation.mutate({
          id: Number(event.id),
          new_cleaner: targetCleanerId,
          old_cleaner: resolveCleanerId(oldEvent?.resource),
        });
      }
    },
    [
      resolveCleanerId,
      updateBookingSessionMutation,
      hasTimeOffOverlap,
      hasBookingOverlap,
      eventUpdateFail,
    ]
  );
  // this fires when drag and drop event between cleaners
  // that's where we update the database if the event is non-recurring
  const handleEventUpdated = useCallback(
    (event: MbscEventUpdatedEvent, inst: EventcalendarBase) => {
      const targetCleanerId = resolveCleanerId(event.event.resource);
      const newStart = event.event.start;
      const newEnd = event.event.end;

      if (hasTimeOffOverlap(targetCleanerId, newStart, newEnd)) {
        eventUpdateFail();
        if (event.oldEvent) {
          inst.updateEvent(event.oldEvent);
        }
        return;
      }

      if (
        hasBookingOverlap(targetCleanerId, newStart, newEnd, event.event.id)
      ) {
        eventUpdateFail();
        if (event.oldEvent) {
          inst.updateEvent(event.oldEvent);
        }
        return;
      }

      if (!event.event.recurring) {
        updateBookingSessionMutation.mutate({
          id: Number(event.event.id),
          new_cleaner: targetCleanerId,
          old_cleaner: resolveCleanerId(event.oldEvent?.resource),
        });
      }
    },
    [
      resolveCleanerId,
      updateBookingSessionMutation,
      hasTimeOffOverlap,
      hasBookingOverlap,
      eventUpdateFail,
    ]
  );

  // this fires when drag and drop event from sidebar to calendar or recurrring DND event is created
  const handleEventCreate = useCallback(
    (args: MbscEventCreateEvent, inst: EventcalendarBase) => {
      const cachedUnassignedJobs: TransformedBooking[] | undefined =
        queryClient.getQueryData(['unassignedJobs']);

      const targetCleanerId = resolveCleanerId(args.event.resource);

      if (
        hasTimeOffOverlap(targetCleanerId, args.event.start, args.event.end)
      ) {
        eventUpdateFail();
        inst.removeEvent(args.event);
        return;
      }

      if (
        hasBookingOverlap(targetCleanerId, args.event.start, args.event.end)
      ) {
        eventUpdateFail();
        inst.removeEvent(args.event);
        return;
      }

      queryClient.setQueryData(
        ['unassignedJobs'],
        (cachedUnassignedJobs || []).filter(
          (unassignedJob) => unassignedJob.id !== args.event.id
        )
      );
      updateBookingSessionMutation.mutate({
        id: Number(args.event.id),
        new_cleaner: targetCleanerId,
        old_cleaner: 0,
      });
    },
    [
      queryClient,
      resolveCleanerId,
      updateBookingSessionMutation,
      hasTimeOffOverlap,
      hasBookingOverlap,
      eventUpdateFail,
    ]
  );

  // ====================
  //       SKELETON
  // ====================
  if (isBookingSessionsDataLoading) {
    return <SchedulerPageSkeleton />;
  }
  // ===================

  return (
    <Box sx={{ width: '100%' }}>
      {schedulerRestrictionWarningMessages.length > 0 && (
        <Alert
          severity="warning"
          onClose={() => setSchedulerRestrictionWarningMessages([])}
          sx={{ mb: 2 }}
        >
          <Stack spacing={0.5}>
            {schedulerRestrictionWarningMessages.map((message) => (
              <Typography key={message} variant="body2">
                {message}
              </Typography>
            ))}
          </Stack>
        </Alert>
      )}

      <Stack direction="row" sx={{ position: 'relative' }} width={'100%'}>
        <Box
          sx={[
            schedulerSx,
            {
              [`& .${RESOURCE_HIGHLIGHT_CLASS}, & .mbsc-timeline-resource:has([data-scheduler-highlighted-cleaner="true"]), & .mbsc-timeline-resource-cell:has([data-scheduler-highlighted-cleaner="true"])`]:
                {
                  backgroundColor: `${highlightedResourceBackground} !important`,
                  boxShadow: `inset 4px 0 0 ${theme.palette.info.main}`,
                },
              [`& .mbsc-timeline-resource-cell:has([data-scheduler-highlighted-cleaner="true"]) > *`]:
                {
                  width: '100%',
                },
              [`& .${RESOURCE_HIGHLIGHT_CLASS} .mbsc-timeline-resource-cell`]: {
                backgroundColor: highlightedResourceBackground,
                boxShadow: `inset 4px 0 0 ${theme.palette.info.main}`,
              },
            },
          ]}
          ref={schedulerScrollRef}
          onDragOverCapture={handleSchedulerDragOver}
          onDragLeave={stopSchedulerAutoScroll}
          onDragEnd={stopSchedulerAutoScroll}
          onDrop={stopSchedulerAutoScroll}
        >
          <Eventcalendar
            dataTimezone="utc"
            displayTimezone="utc"
            timeFormat="HH:mm"
            view={schedulerViewConfig}
            data={schedulerEvents}
            invalid={visibleProviderTimeOffEvents}
            resources={resourcesWithHighlight}
            //
            dragToMove={true}
            dragInTime={false}
            dragToResize={false}
            eventOverlap={false}
            showEventTooltip={false}
            externalDrop={true}
            externalDrag={true}
            //
            selectedDate={selectedDate}
            onSelectedDateChange={handleSelectedDateChange}
            //
            renderScheduleEvent={renderScheduleEvent}
            renderHeader={renderHeader}
            renderResource={renderResource}
            //
            onEventClick={handleEventClick}
            onEventDoubleClick={handleEditSession}
            onEventUpdate={handleEventUpdate}
            onEventUpdated={handleEventUpdated} // https://docs.mobiscroll.com/react/eventcalendar#event-onEventUpdated
            onEventCreate={handleEventCreate}
            onEventUpdateFailed={eventUpdateFail}
            // themes
            theme="ios"
            themeVariant="light"
          />
        </Box>

        <Box
          sx={{
            width: { xs: '100%', lg: 420 },
            flexShrink: 0,
            maxHeight: { lg: 'calc(100vh - 120px)' },
            overflow: 'hidden',
            position: { lg: 'sticky' },
            top: { lg: theme.spacing(2) },
          }}
        >
          <Stack
            spacing={0}
            sx={{
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              overflow: 'hidden',
              height: '100%',
              backgroundColor: 'background.paper',
            }}
          >
            <Tabs
              value={quickActionTab}
              onChange={(_e, value: 'assign' | 'overlaps') =>
                setQuickActionTab(value)
              }
              variant="fullWidth"
              textColor="primary"
              indicatorColor="primary"
            >
              <Tab value="assign" label="Suggestions & Unassigned" />
              <Tab value="overlaps" label="Overlap report" />
            </Tabs>
            <Divider />
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                p: 2,
                display: quickActionTab === 'assign' ? 'block' : 'none',
              }}
              ref={recommendationsCardRef}
            >
              <Stack spacing={2}>
                <SuggestedProvidersCard
                  booking={selectedBooking ?? undefined}
                  summary={selectedSummary ?? undefined}
                  matches={selectedMatches}
                  reason={selectedMatchesReason}
                  isLoading={selectedMatchesLoading}
                  pinnedProviderIds={
                    selectedBooking?.pinnedProviders?.map(
                      (provider) => provider.provider_id
                    ) ?? []
                  }
                  onPin={(provider) =>
                    selectedBooking &&
                    handlePinProvider(selectedBooking.id, provider)
                  }
                  onUnpin={(providerId) =>
                    selectedBooking &&
                    handleUnpinProvider(selectedBooking.id, providerId)
                  }
                  isCollapsed={!showSuggestionsPanel}
                  onToggleCollapse={handleToggleSuggestionsPanel}
                  onAssign={
                    selectedBooking ? handleAssignSuggestedProvider : undefined
                  }
                />

                <UnassignedEventsContainer
                  selectedDate={selectedDate}
                  onNavigateToDate={handleDateSelection}
                  onSelectSession={handleSelectUnassignedSession}
                  selectedSessionId={selectedBooking?.id ?? null}
                />
              </Stack>
            </Box>

            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                p: 2,
                display: quickActionTab === 'overlaps' ? 'block' : 'none',
              }}
            >
              <ProviderOverlapsPanel />
            </Box>
          </Stack>
        </Box>
      </Stack>

      {/* DIALOG */}
      <CleaningJobDetailsDialog
        isJobDialogOpen={isJobDialogOpen}
        closeJobDialog={closeJobDialog}
        currentSessionId={currentSessionId}
      />
    </Box>
  );
}
